import React, { useEffect, useState } from 'react'
import { Platform } from 'react-native'

import { zodResolver } from '@hookform/resolvers/zod'
import {
  DateTimePickerAndroid,
  DateTimePickerEvent,
} from '@react-native-community/datetimepicker'
import { useRoute } from '@react-navigation/native'
import KeyBoardWrapper from '@zet/common/components/keyboardWrapper'
import LazyHOC from '@zet/common/components/lazyHoc'
import ScreenViewEventWrapper from '@zet/common/components/screenViewEventWrapper'
import {
  useAddCustomerNextState,
  useAddCustomerNextStateWeb,
} from '@zet/customer/src/api/hooks'
import useAddCustomerStore from '@zet/customer/src/store/addCustomer'
import { Button, Icon, colors as zetColors } from '@zet/design-system'
import { Input } from '@zet/design-system/input'
import { Stack, YStack } from '@zet/design-system/layout'
import Pressable from '@zet/design-system/pressable'
import { B1, B2, H5 } from '@zet/design-system/typography'
import { WebDatePicker } from '@zet/design-system/webDatePicker'
import { i18n } from '@zet/localization'
import moment from 'moment'
import { useUnsafeVariable } from 'nativewind'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'

import AddCustomerBackHandlerHOC from '../../components/addCustomerBackHandlerHOC'
import FormValidationErrorMessage from '../../components/formValidationErrorMessage'

const validationSchema = z
  .object({
    panNumber: z
      .string()
      .min(1, { message: 'Field is required!' })
      .max(10)
      .regex(
        new RegExp(/(^([a-zA-Z]{5})([0-9]{4})([a-zA-Z]{1})$)/),
        'Invalid PAN'
      ),

    /**
     * DOB can be matched with either the DD/MM/YYYY or YYYY-MM-DD format.
     */

    dob: z
      .string({ required_error: '' })
      .regex(
        new RegExp(
          /^(?:(?:(?:0[1-9]|1[0-9]|2[0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4})|(?:(?:\d{4})-(0[1-9]|1[0-2])-(0[1-9]|1[0-9]|2[0-9]|3[01])))$/
        ),
        'Please select a valid Date'
      ),
  })
  .required()

type ValidationSchema = z.infer<typeof validationSchema>

const PanDetailsV2 = () => {
  const MIN_ELIGIBLE_DATE = moment().subtract(15, 'years').format('YYYY-MM-DD')
  const today = new Date()

  const [date, setDate] = useState<Date>(today)
  const onChange = (
    event: DateTimePickerEvent,
    selectedDate: Date | undefined
  ) => {
    const value = selectedDate || new Date()
    setDate(value)
    setValue('dob', moment(value).format('DD/MM/YYYY'), {
      shouldValidate: true,
      shouldTouch: true,
    })
  }

  const showMode = () => {
    DateTimePickerAndroid.open({
      value: new Date(MIN_ELIGIBLE_DATE) || undefined,
      onChange,
      mode: 'date',
      maximumDate: new Date(MIN_ELIGIBLE_DATE),
    })
  }
  const {
    control,
    formState: { errors, isValid },
    handleSubmit,
    watch,
    setError,
    clearErrors,
    setValue,
  } = useForm<ValidationSchema>({
    mode: 'onTouched',
    resolver: zodResolver(validationSchema),
  })
  const { params } = useRoute()
  const [colorPrimary100] = useUnsafeVariable('--color-primary-100')
  const { mutate, data, isLoading } = useAddCustomerNextState()
  const { mutate: mutateForWeb, isLoading: isLoadingCrif } =
    useAddCustomerNextStateWeb()

  // screenName
  useEffect(() => {
    if (data?.actionData?.panNo) {
      setValue('panNumber', data?.actionData?.panNo)
    }
  }, [data, setValue])
  const { isWebFlow } = useAddCustomerStore((store) => store)

  // On web referesh this useeffect will run
  useEffect(() => {
    if (
      Platform.OS === 'web' &&
      !isWebFlow &&
      data?.UI_isAddCustomerNewFlow &&
      params?.customerId
    ) {
      mutateForWeb({
        customerId: params?.customerId,
        screenName: 'GET_CURRENT_STATE',
        fullName: params?.customerName,
        phoneNumber: params?.customerPhoneNumber,
      })
    }
  }, [
    data?.UI_isAddCustomerNewFlow,
    isWebFlow,
    mutateForWeb,
    params?.customerId,
  ])

  const onsubmit = () => {
    const { panNumber, dob } = watch()
    if (Platform.OS === 'web') {
      const urlParams = new URLSearchParams(window.location.search)
      const hash = urlParams.get('hashNew')
      mutateForWeb(
        {
          panNo: panNumber,
          customerId: params?.customerId,
          dob: moment(dob).format('YYYY-MM-DD'),
          screenName: 'SHOWN_PAN_COLLECTION_SCREEN_V2',
          hash: hash as string,
        },
        {
          onError: (err) => {
            if (err.response.data.error.code === 400) {
              setError('panNumber', {
                message: err.response.data.error.message,
              })
            }
          },
          onSuccess: (data) => {
            if (data?.code === 400) {
              setError('panNumber', {
                message: data?.message,
              })
            }
          },
        }
      )
    } else {
      mutate(
        {
          panNo: panNumber,
          customerId: params?.customerId,
          dob: moment(dob, 'DD/MM/YYYY').format('YYYY-MM-DD'),
          screenName: 'SHOWN_PAN_COLLECTION_SCREEN_V2',
          isDemoSale: params?.isDemoSale,
        },
        {
          onError: (err) => {
            if (err.response.data.error.code === 400) {
              setError('panNumber', {
                message: err.response.data.error.message,
              })
            }
          },
          onSuccess: (data) => {
            if (data?.code === 400) {
              setError('panNumber', {
                message: data?.message,
              })
            }
          },
        }
      )
    }
  }
  return (
    <LazyHOC>
      <ScreenViewEventWrapper screenName={'web_new_add_customer_pan_details'}>
        <AddCustomerBackHandlerHOC isDemoSale={params?.isDemoSale}>
          <KeyBoardWrapper
            className="flex-1 bg-white"
            BottomSheet={
              <Stack className="border-t-grays-30 border-t px-3  py-2">
                <Button
                  size="lg"
                  className="w-full"
                  disabled={!isValid || isLoading || isLoadingCrif}
                  loading={isLoading || isLoadingCrif}
                  //   onPress={handleSubmit(onSubmit)}
                  onPress={handleSubmit(onsubmit)}
                  style={{
                    backgroundColor: !isValid
                      ? zetColors.grays[50]
                      : colorPrimary100,
                  }}
                  buttonEvent={{
                    eventName: 'click_action',
                    payload: {
                      item_name:
                        Platform.OS === 'android'
                          ? 'new_add_customer_pan_details'
                          : 'web_new_add_customer_pan_details',
                    },
                  }}
                  loaderText={i18n.t('customerDetails.verifying')}
                >
                  {i18n.t('bottomView.continue')}
                </Button>
              </Stack>
            }
          >
            <Stack className="flex-1 bg-white">
              <H5 className="border-grays-30 text-grays-100 border-b p-3">
                {Platform.OS === 'web'
                  ? i18n.t('webFlow.panHeader.header')
                  : i18n.t('customerDetails.panScreen.heading')}
              </H5>
              <YStack className="mt-3 px-3">
                <Stack className="mt-1">
                  <B2 className="text-grays-100 mb-2">
                    {Platform.OS === 'web'
                      ? i18n.t('webFlow.basicDetails.pan')
                      : i18n.t('customerDetails.pan')}
                    <B1 className="text-error-100 ml-1">*</B1>
                  </B2>

                  <Controller
                    control={control}
                    render={({
                      field: { onChange, onBlur, value },
                      fieldState: { isTouched },
                    }) => (
                      <Input
                        placeholder="Eg: NVLNK4546"
                        onBlur={onBlur}
                        onChangeText={(value) => {
                          clearErrors('panNumber')
                          onChange(value)
                        }}
                        size='md'
                        value={value}
                        type={
                          errors?.panNumber?.message && isTouched
                            ? 'error'
                            : errors?.panNumber?.message ===
                              'PAN already exists'
                            ? 'warning'
                            : 'primary'
                        }
                        autoCapitalize="characters"
                        maxLength={10}
                      />
                    )}
                    name="panNumber"
                  />

                  <FormValidationErrorMessage
                    error={errors?.panNumber?.message}
                    key={errors?.panNumber?.message}
                  />
                </Stack>
              </YStack>

              {Platform.OS === 'android' ? (
                <Stack className="mx-3 mt-4">
                  <B2 className="text-grays-80 mb-2">
                    {i18n.t('customerDetails.dob')}
                    <B1 className="text-error-100 ml-1"> *</B1>
                  </B2>
                  <Controller
                    control={control}
                    name="dob"
                    render={({ field: { onBlur, onChange, value } }) => (
                      <Pressable onPress={showMode}>
                        <Input
                          isEditable={false}
                          placeholder="Select Date"
                          iconRight={
                            <Icon
                              type="ArrowDown"
                              color={zetColors.grays[80]}
                              className="mr-2"
                            />
                          }
                          iconLeft={
                            <Icon
                              type="DayOutlined"
                              color={zetColors.grays[50]}
                              className="mr-2 border"
                            />
                          }
                          size='md'
                          className="justify-center"
                          style={{
                            color:
                              date === today
                                ? zetColors.grays[50]
                                : zetColors.grays[100],
                          }}
                          value={value}
                        />
                      </Pressable>
                    )}
                  />
                  <FormValidationErrorMessage
                    error={errors?.dob?.message}
                    key={errors?.dob?.message}
                  />
                </Stack>
              ) : (
                <Stack className="mx-3 mt-3">
                  <B2 className="text-grays-80 mb-1">
                    {i18n.t('webFlow.basicDetails.dob')}
                    <B1 className="text-error-100 ml-1">*</B1>
                  </B2>
                  <Controller
                    control={control}
                    name="dob"
                    render={({ field: { value, onChange, onBlur } }) => (
                      <>
                        <Icon
                          type="CalenderOutlined"
                          color={zetColors.grays[50]}
                          className="absolute mt-6 pl-2 pt-1"
                          size={31}
                        ></Icon>
                        {/* {!value && (
                          <Stack className="absolute left-6 mt-6 pt-1">
                            <SH1 className="text-grays-50">DD/MM/YYYY</SH1>
                          </Stack>
                        )} */}
                        <WebDatePicker
                          onBlur={onBlur}
                          onChange={(v) => onChange(v)}
                          value={value}
                          maxDate={MIN_ELIGIBLE_DATE}
                        />
                      </>
                    )}
                  />
                  <FormValidationErrorMessage
                    error={errors?.dob?.message}
                    key={errors?.dob?.message}
                  />
                </Stack>
              )}
            </Stack>
          </KeyBoardWrapper>
        </AddCustomerBackHandlerHOC>
      </ScreenViewEventWrapper>
    </LazyHOC>
  )
}

export default PanDetailsV2
